/**
 * SurfaceRuleEditor — pick the default and the other allowed policies for one kind of work.
 * Template example: demo `/form-layouts` inside a `Modal` (demo `/ui-elements/dialogs`).
 */
import type React from 'react';
import { z } from 'zod';
import {
  Form,
  FormActions,
  MultiSelectFormField,
  SelectFormField,
  useZodForm,
} from '../../../components/form/index.js';
import type { ModelRuntimePolicy, RuntimePolicySurfaceRuleWrite } from '../../../generated/api/types.gen.js';
import { zRuntimePolicySurfaceRuleWrite } from '../../../generated/api/zod.gen.js';

/** The API's rule body, with the allowed list split into "the default" and "also allowed". */
const ruleFormSchema = zRuntimePolicySurfaceRuleWrite.omit({ allowedPolicyIds: true }).extend({
  alsoAllowed: z.array(z.number()),
});

interface SurfaceRuleEditorProps {
  /** The rule the edit starts from: the system's for a system edit, the organization's (else the system's) for its own. */
  rule: RuntimePolicySurfaceRuleWrite;
  /** Policies that may be chosen (active ones). */
  policies: readonly ModelRuntimePolicy[];
  /** Saves the rule; rejects with the server's error, which the form shows. */
  onSave: (rule: RuntimePolicySurfaceRuleWrite) => Promise<unknown>;
  onCancel: () => void;
}

export const SurfaceRuleEditor: React.FC<SurfaceRuleEditorProps> = ({ rule, policies, onSave, onCancel }) => {
  const form = useZodForm(ruleFormSchema, {
    defaultValues: {
      defaultPolicyId: rule.defaultPolicyId,
      alsoAllowed: rule.allowedPolicyIds.filter((id) => id !== rule.defaultPolicyId),
    },
  });
  const options = policies.map((p) => ({ value: p.id, label: p.name }));

  return (
    <Form
      form={form}
      onSubmit={async ({ defaultPolicyId, alsoAllowed }) => {
        // The default is always allowed; the server enforces the same.
        const others = alsoAllowed.filter((id) => id !== defaultPolicyId);
        await onSave({ defaultPolicyId, allowedPolicyIds: [defaultPolicyId, ...others] });
      }}
    >
      <SelectFormField name="defaultPolicyId" label="Default policy" options={options} required />
      <MultiSelectFormField
        name="alsoAllowed"
        label="Also allowed"
        description="The default policy is always allowed."
        placeholder="No other policies"
        options={options}
      />
      <FormActions submitLabel="Save rule" onCancel={onCancel} />
    </Form>
  );
};
